import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { getQueryFilterByCustomerId } from '@/components/CustomerSelect'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useAdminAccess } from '@/hooks/access'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import adminResources from '@/modules/admin/resources'
import type { Address } from '@/modules/admin/resources'

import queries from '../queries'

import { AddressListItem } from './AddressListItem'

export const AddressList: FC<{
  searchFilter: string
  onClickAddressRow: ({ addressId, isOpen }: { addressId: number; isOpen: boolean }) => void
}> = ({ searchFilter, onClickAddressRow }) => {
  useAdminAccess()
  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<keyof Address>(
    'asc',
    'address'
  )
  const [queryCustomer] = useSearchParamsByKey('company')

  const translate = useTranslation()
  const { locale } = useAppContext()

  const {
    data: addressList,
    isLoading,
    isError,
  } = useQuery(queries.adminAddresses(), adminResources.getAddressList)

  const stringMatcher = (item: Address) => [
    item.address,
    item.zip,
    item.city,
    item.countryName || '',
    item.customerName || '',
    item.isGlobal ? translate('Global') : translate('Local'),
  ]

  const itemsFiltered =
    addressList?.filter(address => {
      const customerFilter =
        queryCustomer === 'global'
          ? address.isGlobal
          : getQueryFilterByCustomerId(queryCustomer, address.customerId)
      return getQueryFilter(searchFilter, stringMatcher)(address) && customerFilter
    }) || []
  const sortedList = getSortedList({ list: itemsFiltered, querySort, querySortBy, locale })
  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [], 30)

  if (isLoading) {
    return <LoaderCircle />
  }

  if (isError || itemsFiltered.length <= 0) {
    return <NoResults />
  }

  return (
    <>
      <ResultCount count={itemsFiltered.length} />
      <Table rowHeight="medium">
        <thead>
          <tr>
            <Th id="address" onClick={handleSort}>
              <Translation id="address" />
            </Th>

            <Th className="hidden md:table-cell" id="city" onClick={handleSort}>
              <Translation id="city" />
            </Th>

            <Th className="hidden md:table-cell" id="zip" onClick={handleSort}>
              <Translation id="zip code" />
            </Th>

            <Th className="hidden md:table-cell" id="countryId" onClick={handleSort}>
              <Translation id="country" />
            </Th>

            <Th id="isGlobal" onClick={handleSort}>
              <Translation id="type" />
            </Th>
          </tr>
        </thead>
        <tbody>
          {visibleList.map(item => (
            <AddressListItem
              key={item.id}
              searchFilter={searchFilter}
              onAddressEdit={() => onClickAddressRow({ addressId: item.id, isOpen: true })}
              {...item}
            />
          ))}
        </tbody>
      </Table>
      <ListLoaderComponent />
    </>
  )
}
